import type { BookingResponse, Flight, Leg } from '@models/landing/api/api-response.model';
import { getAirportCityByCode } from '@utils/flight-util';
import {
  useMasterDataStore,
  selectTerminals,
  selectAirports,
  selectFareBrands,
} from '@store/master-data.store';
import type { Airport, FareBrand } from '@models/master-data/master-data.model';

interface Pax {
  isVisaRunPax?: boolean;
}
export interface ExtractedFlightLegData {
  arrivalDate: string;
  departureDate: string;
  origin: string;
  destination: string;
  originCity: string;
  destinationCity: string;
  sellingCarrier: string;
  flightNum: string;
  originTerminal: string | null;
  destinationTerminal: string | null;
  numberOfStops: number;
  fare: FareType;
  duration: string;
  originName: string;
  destinationName: string;
  physicalFlightID: string;
  pax: Pax[];
  isInterline: boolean;
}

interface FareType {
  fareBasisCode: string;
  webFareType: string;
}

export interface ExtractedFlightData {
  origin: string;
  destination: string;
  originCity: string;
  destinationCity: string;
  stops: Flight['stops'];
  legs: ExtractedFlightLegData[];
  remainingTimeToCheckin: string;
  logicalFlightID: string;
  originName: string;
  destinationName: string;
}

export interface TerminalItem {
  terminal: string;
  display: string;
  context?: string[];
}

const extractUsedLegData = (leg: Leg): ExtractedFlightLegData => {
  const originCity = getAirportCityByCode(leg.origin);
  const destinationCity = getAirportCityByCode(leg.destination);
  return {
    arrivalDate: leg.arrivalDate,
    departureDate: leg.departureDate,
    origin: leg.origin,
    destination: leg.destination,
    originCity: originCity.city,
    destinationCity: destinationCity.city,
    originName: originCity.value ?? '',
    destinationName: destinationCity.value ?? '',
    sellingCarrier: leg.sellingCarrier,
    flightNum: leg.flightNum,
    originTerminal: leg.originTerminal,
    destinationTerminal: leg.destinationTerminal,
    numberOfStops: leg.numberOfStops,
    fare: leg.fare,
    pax: leg.pax,
    duration: leg.duration,
    physicalFlightID: leg.physicalFlightID,
    isInterline: leg.isInterline,
  };
};

export const getAirportsItems = (): Airport[] => {
  return selectAirports(useMasterDataStore.getState());
};

export const getFareBrands = (): FareBrand[] => {
  return selectFareBrands(useMasterDataStore.getState());
};

export const getTerminals = (): TerminalItem[] => {
  return selectTerminals(useMasterDataStore.getState()) as TerminalItem[];
};

const extractUsedFlightData = (flight: Flight, timeToCheckIn: string): ExtractedFlightData => {
  const fromCityName = getAirportCityByCode(flight.origin);
  const toCityName = getAirportCityByCode(flight.destination);
  return {
    remainingTimeToCheckin: timeToCheckIn ?? '',
    origin: flight?.origin ?? '',
    destination: flight?.destination ?? '',
    originCity: fromCityName.city ?? '',
    originName: fromCityName.value ?? '',
    destinationCity: toCityName.city ?? '',
    destinationName: toCityName.value ?? '',
    stops: flight.stops,
    logicalFlightID: flight.logicalFlightID,
    legs: flight.legs.map((leg: Leg) => extractUsedLegData(leg)),
  };
};

export const extractUsedFlightDataFromBookingResponse = (
  booking: BookingResponse,
): ExtractedFlightData[] => {
  return booking.flights.map((flight: Flight) =>
    extractUsedFlightData(flight, booking.remainingTimeToCheckin),
  );
};

export const mapBookingResponseToFlightDetails = (
  booking: BookingResponse,
): ExtractedFlightData[] => {
  return extractUsedFlightDataFromBookingResponse(booking);
};
